import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
  Font
} from "@react-pdf/renderer"
import React from "react"

import DateTime from "../../utils/DateTime"

import font1 from "./fonts/Roboto-Regular.ttf"
import font1i from "./fonts/Roboto-Italic.ttf"
import font1b from "./fonts/Roboto-Bold.ttf"
import font2b from "./fonts/RobotoCondensed-Bold.ttf"
import font2l from "./fonts/RobotoCondensed-Light.ttf"
import icons from "./fonts/MaterialIcons-Regular.ttf"

Font.register({
  family: "Roboto",
  fonts: [
    { src: font1, format: "truetype", fontStyle: "normal", fontWeight: 400 },
    { src: font1i, format: "truetype", fontStyle: "italic", fontWeight: 400 },
    { src: font1b, format: "truetype", fontStyle: "normal", fontWeight: 700 }
  ]
})
Font.register({
  family: "Roboto Condensed",
  fonts: [
    { src: font2b, format: "truetype", fontStyle: "normal", fontWeight: 700 },
    { src: font2l, format: "truetype", fontStyle: "normal", fontWeight: 300 }
  ]
})
Font.register({ family: "Material Icons", format: "truetype", src: icons })

const styles = StyleSheet.create({
  body: {
    padding: "10mm 10mm 15mm 10mm",
    fontFamily: "Roboto",
    fontSize: "10pt",
    color: "#333"
  },
  header: {
    marginBottom: "5mm"
  },
  headerImage: {
    width: "100%",
    height: "auto"
  },
  logo: {
    width: 120,
    height: 60,
    position: "absolute",
    right: 0,
    top: 0
  },
  footer: {
    position: "absolute",
    fontSize: 9,
    bottom: "5mm",
    left: "10mm",
    right: "10mm",
    textAlign: "center",
    color: "grey"
  },
  titleSection: {
    textAlign: "center",
    marginVertical: "10mm"
  },
  title: {
    fontFamily: "Roboto Condensed",
    fontSize: "24pt",
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#005e91"
  },
  subtitle: {
    fontFamily: "Roboto Condensed",
    fontSize: "18pt",
    textTransform: "uppercase",
    fontWeight: 300
  },
  h1: {
    fontFamily: "Roboto Condensed",
    fontSize: "20pt",
    textTransform: "uppercase",
    color: "#0081c6",
    fontWeight: 700
  },
  h2: {
    fontFamily: "Roboto Condensed",
    fontSize: "16pt",
    textTransform: "uppercase",
    color: "#005e91",
    fontWeight: 700,
    marginBottom: "3mm"
  },
  h3: {
    fontFamily: "Roboto Condensed",
    fontSize: "12pt",
    textTransform: "uppercase",
    color: "#47a3d3",
    fontWeight: 700,
    marginBottom: "2mm"
  },
  italic: {
    fontStyle: "italic"
  },
  bold: {
    fontWeight: "bold"
  },
  infoBox: {
    border: "1px solid #eee",
    borderRadius: 3,
    padding: "5mm",
    marginBottom: "5mm",
    width: "60%",
    alignSelf: "center"
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: "1mm"
  },
  table: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    border: "1pt solid #ccc"
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0081c6",
    color: "#ffffff",
    padding: "2mm",
    fontWeight: "bold",
    borderBottom: "1pt solid #ccc"
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #eee",
    padding: "2mm"
  },
  tableCell: {
    fontSize: "9pt"
  },
  colDescription: { width: "70%" },
  colItemCode: { width: "15%", textAlign: "left" },
  colAmount: { width: "15%", textAlign: "right" },
  acceptanceSection: {
    marginTop: "10mm"
  },
  acceptanceText: {
    fontSize: 10,
    lineHeight: 1.5,
    marginBottom: "5mm"
  }
})

const PartB_AcceptancePage = props => (
  <Page size="A4" style={styles.body} break>
    <View style={styles.header} fixed>
      <Image style={styles.headerImage} src={`${props.headerImages}`} />
      <Image
        style={styles.logo}
        src="https://www.goodx.co.za/wp-content/uploads/2021/04/Goodx-Logo-dark.png"
      />
    </View>
    <Text style={styles.h2}>
      PART B: Information of Acceptance of the agreement
    </Text>
    <View style={styles.infoBox}>
      <View style={styles.infoRow}>
        <Text style={styles.bold}>Name:</Text>
        <Text>{props.agreementName}</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.bold}>Surname:</Text>
        <Text>{props.agreementSurname}</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.bold}>ID No:</Text>
        <Text>{props.agreementID}</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.bold}>Role:</Text>
        <Text>{props.agreementRole}</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.bold}>Cell No:</Text>
        <Text>{props.agreementCell}</Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.bold}>Place:</Text>
        <Text>{props.agreementPlace}</Text>
      </View>
    </View>

    <View style={styles.acceptanceSection}>
      <Text style={styles.h3}>Acceptance of Terms of Agreement</Text>
      <Text style={styles.acceptanceText}>
        I, <Text style={styles.bold}>{props.agreementContact}</Text>, by
        clicking on the accept button, confirm that the information supplied is
        correct, that I have read and accept the terms of this GoodX Software
        Standard Licence, Services & Operator Agreement read together with the
        General Terms and Conditions, Policies and Standard Operating Procedures
        as amended from time to time on the official GoodX website, and that I
        have the authority to sign on behalf of the Client.
      </Text>
    </View>

    <Text
      style={styles.footer}
      fixed
      render={({ pageNumber, totalPages }) =>
        `GoodX Software Licence & Services Agreement for ${props.props?.site_name} | Page ${pageNumber} of ${totalPages}`
      }
    />
  </Page>
)

const ContractTemplate = props => {
  const contract = props?.props
  const contractLines = props?.line_detail || []
  const headerImage = props?.headerImages

  const groupedLines = contractLines.reduce((acc, current) => {
    const key = current.item_type_description
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(current)
    return acc
  }, {})

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <View style={styles.header} fixed>
          <Image style={styles.headerImage} src={`${headerImage}`} />
          <Image
            style={styles.logo}
            src="https://www.goodx.co.za/wp-content/uploads/2021/04/Goodx-Logo-dark.png"
          />
        </View>

        <View style={styles.titleSection}>
          <Text style={styles.subtitle}>
            GoodX Software Standard Licence, Services & Operator Agreement
          </Text>
          <Text style={styles.title}>between:</Text>
          <Text> </Text>
          <Text style={styles.h1}>{contract?.company_type}</Text>
          <Text style={styles.italic}>
            (hereinafter referred to as "GoodX")
          </Text>
          <Text> </Text>
          <Text>And</Text>
          <Text> </Text>
          <Text style={styles.h1}>{contract?.debtor_name}</Text>
          <Text style={styles.italic}>
            (hereinafter referred to as "the Client")
          </Text>
          <Text> </Text>
          <Text style={styles.italic}>
            (hereinafter referred to as "the Parties")
          </Text>
        </View>

        <View style={styles.infoBox}>
          <View style={styles.infoRow}>
            <Text style={styles.bold}>Contract to:</Text>
            <Text>{contract?.debtor_name}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.bold}>Tel:</Text>
            <Text>{contract?.tel_no}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.bold}>E-mail:</Text>
            <Text>{contract?.email}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.bold}>Date:</Text>
            <Text>{DateTime.curTime}</Text>
          </View>
        </View>

        <Text style={styles.h2} break>
          Part A
        </Text>

        {Object.entries(groupedLines).map(([description, items]) => (
          <View key={description} style={{ marginBottom: "5mm" }} wrap={false}>
            <Text style={styles.h3}>{description}</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader} fixed>
                <Text style={[styles.tableCell, styles.colDescription]}>
                  Description
                </Text>
                <Text style={[styles.tableCell, styles.colItemCode]}>
                  Item Code
                </Text>
                <Text style={[styles.tableCell, styles.colAmount]}>Amount</Text>
              </View>
              {items.map(groupItem =>
                groupItem.line_detail.map(item => (
                  <View
                    style={styles.tableRow}
                    key={item.billable_price.item_code}
                  >
                    <Text style={[styles.tableCell, styles.colDescription]}>
                      {item.billable_price.item_description}
                    </Text>
                    <Text style={[styles.tableCell, styles.colItemCode]}>
                      {item.billable_price.item_code}
                    </Text>
                    <Text style={[styles.tableCell, styles.colAmount]}>
                      {Number(item.billable_price.amount).toFixed(2)}
                    </Text>
                  </View>
                ))
              )}
            </View>
          </View>
        ))}

        <Text
          style={styles.footer}
          fixed
          render={({ pageNumber, totalPages }) =>
            `GoodX Software Licence & Services Agreement for ${contract?.site_name} | Page ${pageNumber} of ${totalPages}`
          }
        />
      </Page>
      {contract?.stage === "signed" && <PartB_AcceptancePage {...props} />}
    </Document>
  )
}

export default ContractTemplate
